import { LicenseInfo } from '@mui/x-license';
import { History } from 'history';
import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Router } from 'react-router-dom';

import { SPMAssistantProvider } from 'components/Assistant/SPMAssistantProvider';
// eslint-disable-next-line import/no-unassigned-import
import 'components/Assistant/SPMAssistant.css';
import { useSyncAIShellLayout } from 'hooks/useSyncAIShellLayout';
import { PermissionsProvider } from 'contexts/Permissions/Permissions';
import { ChakraWrapper, theme } from 'quotient';
import { QGlobalStyles, QToastProvider, THEME_ID, ThemeProvider } from '@captivateiq/quotient';
import { Events } from 'utils/events';

import { createAppQueryClient } from './configs/react-query';
import { setAppQueryClient } from './services/appQueryClientRef';
import i18n from './i18n';
import { TQRouter } from './TQRouter';

type Props = {
  history: History; // History object from shell app - now required
};

const App: React.VFC<Props> = ({ history }) => {
  LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY || '');
  i18n.changeLanguage('en-US'); // for now until we use user locale
  const [queryClient] = useState(() => createAppQueryClient());
  useSyncAIShellLayout();

  useEffect(() => {
    setAppQueryClient(queryClient);
    return () => setAppQueryClient(null);
  }, [queryClient]);

  useEffect(() => {
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

    Events.track(Events.EVENT.tq.landing_page.displayed, {
      viewport_height: viewportHeight,
      viewport_width: viewportWidth,
    });
  }, []);
  /* eslint-disable react/jsx-max-depth */
  return (
    <I18nextProvider defaultNS="translation" i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <PermissionsProvider>
          <div data-tq-shell style={{ marginRight: 'var(--platform-shell-width, 0px)' }}>
            <ChakraWrapper customStyle={{ display: 'flex', flexDirection: 'column' }} fullHeight isEnabled>
              <ThemeProvider theme={{ [THEME_ID]: theme }}>
                <QGlobalStyles
                  styles={{
                    '.notistack-SnackbarContainer': {
                      zIndex: 9999, // Ensure toasts appear above modals (modals use z-index 1200-1400)
                    },
                  }}
                />
                <QToastProvider>
                  <HelmetProvider>
                    {!process.env.HIDE_REACT_QUERY_DEVTOOLS && (
                      <ReactQueryDevtools buttonPosition="bottom-left" position="bottom" />
                    )}
                    <Router history={history}>
                      <SPMAssistantProvider>
                        <TQRouter />
                      </SPMAssistantProvider>
                    </Router>
                  </HelmetProvider>
                </QToastProvider>
              </ThemeProvider>
            </ChakraWrapper>
          </div>
        </PermissionsProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
};
export default App;
